import { AdUnit } from '@pubstack/common/src/adunit'
import { BidParamsMapping } from '@pubstack/common/src/bid-params-mapping'
import { BIDDER_RULE_SECTION_LABELS, sectionOfBidderSeat } from '@pubstack/common/src/bidder-rule'
import { BidderSeat } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog, isMarketplaceAlias } from '@pubstack/common/src/bidderCatalog'
import { MEDIA_TYPES } from '@pubstack/common/src/media-types'
import { byName } from '@pubstack/common/src/sort'
import { useAtomValue } from 'jotai'
import { FunctionComponent, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useBidderRules } from '~/api/bidder-rules.api'
import { useCatalog } from '~/api/catalog.api'
import { Colors } from '~/assets/style/colors'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { BidderIdentity } from '~/components/catalog/BidderIdentity'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import { CollapsiblePanel } from '~/components/CollapsiblePanel'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { RadioGroup } from '~/components/RadioGroup'
import { Select } from '~/components/Select'
import { Skeleton } from '~/components/Skeleton'
import { Status, StatusState } from '~/components/Status'
import { Tooltip } from '~/components/Tooltip'
import { generateStackNameList, StackNameEntry } from '../../rules/PureUsedStackTooltip'
import { AdmIntegrationMapping } from '../bidderParams/AdmIntegrationMapping'
import { AdmSitePageContext } from '~/modules/pbs/sites/site/useSitePageContext'
import { configurationState } from '~/state'
import { useMediaMappingSeats } from './useMediaMappingSeats'

const MAPPING_LABEL_CLASSES = 'font-rubik text-base/[26px]'

type BidderSeatWithStatus = BidderSeat & {
  totalMappings: number
  usedMappings: number
  statusState: {
    label: 'active' | 'inactive' | 'error' | 'warning'
    order: number
  }
  mappedStacks: StackNameEntry[]
}

const getStatusState = (usedMappings: number, maxMappings: number): { label: StatusState; order: number } => {
  if (usedMappings === 0) {
    return { label: 'inactive', order: 2 }
  }

  if (usedMappings < maxMappings) {
    return { label: 'warning', order: 1 }
  }

  return { label: 'active', order: 0 }
}

const getSeatUsage = (bidderParams: BidParamsMapping[], adUnits: AdUnit[], bidderSeat?: BidderSeat, catalogBidder?: BidderCatalog) => {
  const hasMediaTypes = bidderSeat?.mappingDimensions.includes('mediaType')
  const hasDevices = bidderSeat?.mappingDimensions.includes('device')
  const headerBiddingEnabledAdUnits = adUnits.filter((adUnit) => adUnit.headerBiddingEnabled)
  const maxMappings =
    (hasDevices ? headerBiddingEnabledAdUnits.flatMap((a) => a.devices).length : headerBiddingEnabledAdUnits.length) *
    (hasMediaTypes ? (catalogBidder?.supportedMediaTypes || Array.from(MEDIA_TYPES)).length : 1)
  const usedMappings = bidderParams.filter((bp) => Object.keys(bp.bidParams).length).length
  return {
    usedMappings,
    maxMappings,
  }
}

export const SiteMappingBidderView: FunctionComponent = () => {
  // State
  const { site, adUnits, stacks } = useOutletContext<AdmSitePageContext>()

  // Hooks
  const {
    reset,
    control,
    formState: { isDirty },
  } = useForm<{ search: string; usedOnThisSite: boolean; onlyUsedBidders: boolean; environmentFilter: ('client' | 'server')[] }>({
    defaultValues: {
      search: '',
      usedOnThisSite: true,
      onlyUsedBidders: true,
      environmentFilter: ['client', 'server'],
    },
  })
  const user = useAuthenticatedUser()
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)
  const search = useWatch({ control, name: 'search' })
  const usedOnThisSite = useWatch({ control, name: 'usedOnThisSite' })
  const onlyUsedBidders = useWatch({ control, name: 'onlyUsedBidders' })
  const environmentFilter = useWatch({ control, name: 'environmentFilter' })
  const navigate = useNavigate()

  // API Hooks
  const { data: catalogBidders = [], isLoading: isLoadingCatalogBidders } = useCatalog('adm')
  const { bidderSeats, existingMappings, refetchMappings, isLoading: isLoadingSeats } = useMediaMappingSeats(site.id)
  const { data: bidderRules, isLoading: isLoadingBidderRules } = useBidderRules('adm')

  const searchedBidders = useMemo(
    () => bidderSeats.filter((seat) => seat.name.toLowerCase().includes(search.toLowerCase()) && environmentFilter.includes(seat.platform)),
    [bidderSeats, environmentFilter, search]
  )
  const usedBidders = useMemo(
    () => (onlyUsedBidders ? searchedBidders.filter((seat) => existingMappings.some((mapping) => mapping.bidderSeatId === seat.id && Object.keys(mapping.bidParams).length)) : searchedBidders),
    [searchedBidders, onlyUsedBidders, existingMappings]
  )

  const usedAdUnitsIds = useMemo(
    () =>
      Array.from(
        new Set(
          stacks.flatMap((stack) => [
            ...stack.configuration.desktopAdUnitsIds,
            ...stack.configuration.mobileAdUnitsIds,
            // Roadblock ad units live in separate arrays (mutually exclusive with the regular ones),
            // so an ad unit used only as a roadblock is still used on this site.
            ...(stack.configuration.desktopRoadblockAdUnitsIds ?? []),
            ...(stack.configuration.mobileRoadblockAdUnitsIds ?? []),
          ])
        )
      ),
    [stacks]
  )
  const filteredAdUnits = useMemo(() => (usedOnThisSite ? adUnits.filter(({ id }) => usedAdUnitsIds.includes(id)) : adUnits), [adUnits, usedOnThisSite, usedAdUnitsIds])

  const bidderSeatsWithStatus: BidderSeatWithStatus[] = usedBidders
    .map((seat) => {
      const catalogBidder = catalogBidders.find((c) => c.id === seat.bidderCode)
      const mappingsForThisSeat = existingMappings.filter((mapping) => mapping.bidderSeatId === seat.id)
      const { usedMappings, maxMappings } = getSeatUsage(
        usedOnThisSite ? mappingsForThisSeat.filter((mapping) => mapping.dimensions.adUnitId && usedAdUnitsIds.includes(mapping.dimensions.adUnitId)) : mappingsForThisSeat,
        filteredAdUnits,
        seat,
        catalogBidder
      )
      const mappedStacks: StackNameEntry[] = stacks
        .filter((stack) => {
          return (
            stack.configuration.headerBiddingEnabled &&
            bidderRules?.find((rule) => rule.id === stack.configuration.bidderRuleId)?.selectedBidderSeats?.some((ruleSeat) => ruleSeat.id === seat.id) &&
            mappingsForThisSeat.some((mapping) => mapping.dimensions.adUnitId && usedAdUnitsIds.includes(mapping.dimensions.adUnitId) && Object.keys(mapping.bidParams).length)
          )
        })
        .map((stack) => ({ enabled: stack.status !== 'archived' && stack.status !== 'archived_group', stackName: stack.name, version: stack.version?.toString() ?? 'draft', type: 'adm' as const }))

      return {
        ...seat,
        usedMappings,
        totalMappings: maxMappings,
        statusState: getStatusState(usedMappings, maxMappings),
        mappedStacks,
      }
    })
    .sort((a, b) => a.statusState.order - b.statusState.order || byName(a, b))

  const pipeSeparator = `\xa0 | \xa0`
  const shouldShowEnvironment = useMemo(() => bidderSeats.some((seat) => seat.platform === 'server'), [bidderSeats])

  const onCreateNewBidder = () => navigate('/monetization/inventory/catalog')

  return (
    <>
      <form className="mb-3 flex shrink-0 grow items-center gap-3">
        <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search bidder'} control={control} />
        <Select
          control={control}
          name={'onlyUsedBidders'}
          options={[
            { label: 'Used on this site', value: true, trackingLabel: 'adm-sites-stacks-mapping-bidders-filter-used-apply' },
            { label: 'Active bidders and aliases', value: false, trackingLabel: 'adm-sites-stacks-mapping-bidders-filter-active-apply' },
          ]}
          label={'Bidders and aliases'}
        />
        <Select
          control={control}
          name={'usedOnThisSite'}
          options={[
            { label: 'Used on this site', value: true, trackingLabel: 'adm-sites-stacks-mapping-ad-units-filter-used-apply' },
            { label: 'All ad units', value: false, trackingLabel: 'adm-sites-stacks-mapping-ad-units-no-filter-apply' },
          ]}
          label={'Ad units'}
        />
        {shouldShowEnvironment && (
          <RadioGroup
            variant="secondary"
            control={control}
            name="environmentFilter"
            options={[
              {
                label: 'All sides',
                value: ['client', 'server'],
                trackingLabel: 'adm-site-mapping-all-sides-filter-set',
              },
              {
                label: 'Client-side',
                value: ['client'],
                trackingLabel: 'adm-site-mapping-client-side-filter-set',
                iconName: 'client',
              },
              {
                label: 'Server-side',
                value: ['server'],
                trackingLabel: 'adm-site-mapping-server-side-filter-set',
                iconName: 'server',
              },
            ]}
          />
        )}
        {isDirty && (
          <Tooltip title={'Restore'}>
            <Button
              className="mt-1"
              iconName={'restore'}
              variant={'tertiary'}
              onClick={() => {
                reset()
              }}
              trackingLabel={'adm-sites-stacks-bidders-mapping-filter-restore'}
            />
          </Tooltip>
        )}
      </form>
      <div className="grid gap-2.5">
        {isLoadingCatalogBidders || isLoadingSeats || isLoadingBidderRules ? (
          <>
            {[0, 1, 2].map((e) => (
              <CollapsiblePanel
                key={e}
                hasBorderWhenOpened
                isCollapsible={{ trackingLabel: 'adm-sites-stacks-bidders-mapping-collapse-apply' }}
                title={
                  <div className="flex w-full justify-between">
                    <span className={MAPPING_LABEL_CLASSES}>
                      <Skeleton width={`15ch`} />
                    </span>
                    <span className={MAPPING_LABEL_CLASSES}>
                      <Skeleton width={`15ch`} />
                    </span>
                    <span className={MAPPING_LABEL_CLASSES}>
                      <Skeleton width={`15ch`} />
                    </span>
                  </div>
                }
              />
            ))}
          </>
        ) : bidderSeatsWithStatus.length > 0 ? (
          bidderSeatsWithStatus.map((seat) => {
            const readOnly = isMarketplaceAlias(seat.name) && !user.isAdmin
            const catalogBidder = catalogBidders.find((c) => c.id === seat.bidderCode)
            return (
              <AdmIntegrationMapping
                key={seat.name}
                adUnits={filteredAdUnits}
                catalogBidder={catalogBidder}
                readOnly={readOnly}
                bidderSeat={seat}
                onSaveSuccess={refetchMappings}
                currentSite={site}
                title={
                  <div className="flex w-full justify-between">
                    <div className="flex items-center gap-1 font-rubik text-base/[26px]">
                      {/* The alias stays the heading — two seats of the same bidder differ by nothing else.
                          The logo says whose they are at a glance, and the tooltip carries the bidder and
                          the adapter code for anyone who has to quote them. */}
                      <BidderIdentity baseCDNUrl={STATIC_ASSETS_CDN} code={seat.bidderCode} name={seat.name} bidderName={catalogBidder?.displayName} size={'title'} />
                      {isMarketplaceAlias(seat.name) && <Chip color={Colors.Ghost} variant={'small'} iconLeft={{ name: 'store_front' }} text={'Marketplace'} />}
                      {shouldShowEnvironment && <Chip color={Colors.Ghost} variant={'small'} iconLeft={{ name: seat.platform }} text={BIDDER_RULE_SECTION_LABELS[sectionOfBidderSeat(seat)]} />}
                    </div>

                    <div className="grid w-1/2 grid-cols-3 gap-x-10">
                      <span className={MAPPING_LABEL_CLASSES}>
                        {
                          <Status state={seat.statusState.label}>
                            {seat.usedMappings} of {seat.totalMappings} mappings
                          </Status>
                        }
                      </span>
                      {seat.mappedStacks.length > 0 ? (
                        <Tooltip title={generateStackNameList(seat.mappedStacks).reduce((acc, stack) => [acc, pipeSeparator, stack])} positions={['left', 'bottom', 'top', 'right']}>
                          <span className={MAPPING_LABEL_CLASSES}>
                            {seat.mappedStacks.length !== stacks.length ? (
                              <span>
                                {seat.mappedStacks.length} stack{seat.mappedStacks.length > 1 && 's'}
                              </span>
                            ) : (
                              <span>All stacks ({stacks.length})</span>
                            )}
                          </span>
                        </Tooltip>
                      ) : (
                        <span className={MAPPING_LABEL_CLASSES}>0 stack</span>
                      )}
                    </div>
                  </div>
                }
              />
            )
          })
        ) : (
          <IllustrationMessage
            className="mt-[60px]"
            iconName={'night_sky'}
            iconSize={'190px'}
            title={'No bidders found'}
            message={`Set up a bidder from our catalog${isDirty ? ' or update your filters' : ''}.`}
          >
            <Button iconName={'add_bidder'} variant={'primary'} onClick={onCreateNewBidder} trackingLabel={'adm-sites-stacks-mapping-all-integration-navigate'}>
              Add new bidder
            </Button>
          </IllustrationMessage>
        )}
      </div>
    </>
  )
}
